/**
 * THE MARK — ported from the aesthetic reference (§9, L1817–1889). Owner: S2.
 *
 * PLACEHOLDER. A generated tree-ring whorl that approximates the Ládera mark
 * so layouts can be judged. Replace <Whorl/> with the official SVG from the
 * brand kit before anything ships. Brand rules still apply to the stand-in:
 * no container, no stroke, no drop shadow, no recolouring, min 150px wide
 * for the full lock-up on screens.
 *
 * The two mark colours are fixed brand values (lint-brand allows raw hex in
 * this file for exactly that reason). `progress` 0..1 fills the rings from the
 * centre outwards, which is what the loader uses for byte-accurate progress.
 */
import { useId, type CSSProperties, type JSX, type MouseEventHandler } from "react";

/* ── minimal helpers (copied from aesthetic L226–235, 299–307, 420–437) ── */

type Pt = readonly [number, number];

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function rand() {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (v: number, a = 0, b = 1): number => (v < a ? a : v > b ? b : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const r1 = (v: number): number => Math.round(v * 10) / 10;

// Quadratic midpoint smoothing: soft, organic, and cheap to serialise.
function softPath(pts: readonly Pt[], closed: boolean): string {
  const n = pts.length;
  if (n < 3) return n ? `M${r1(pts[0][0])},${r1(pts[0][1])}` + (n > 1 ? `L${r1(pts[1][0])},${r1(pts[1][1])}` : "") : "";
  const mid = (a: Pt, b: Pt) => `${r1((a[0] + b[0]) / 2)},${r1((a[1] + b[1]) / 2)}`;
  const p = (q: Pt) => `${r1(q[0])},${r1(q[1])}`;
  if (closed) {
    const pts2 = pts[0][0] === pts[n - 1][0] && pts[0][1] === pts[n - 1][1] ? pts.slice(0, -1) : pts;
    const m = pts2.length;
    let d = `M${mid(pts2[m - 1], pts2[0])}`;
    for (let i = 0; i < m; i++) d += `Q${p(pts2[i])} ${mid(pts2[i], pts2[(i + 1) % m])}`;
    return d + "Z";
  }
  let d = `M${p(pts[0])}L${mid(pts[0], pts[1])}`;
  for (let i = 1; i < n - 1; i++) d += `Q${p(pts[i])} ${mid(pts[i], pts[i + 1])}`;
  return d + `L${p(pts[n - 1])}`;
}

/* ── geometry (deterministic, computed once per module) ─────────────── */

interface WhorlRing {
  d: string;
  dash: string;
  off: string;
}

const WHORL: { rings: WhorlRing[]; road: string } = (() => {
  const rings: WhorlRing[] = [];
  const rand = mulberry32(62);
  for (let k = 0; k < 16; k++) {
    const R = 3 + k * 3.35;
    const pts: Pt[] = [];
    for (let i = 0; i < 64; i++) {
      const t = (i / 64) * Math.PI * 2;
      const rr = R * (1 + 0.035 * Math.sin(3 * t + k) + 0.02 * Math.sin(5 * t + 2 * k));
      pts.push([50 + rr * Math.cos(t) * 1.04, 41 + rr * Math.sin(t) * 0.9]);
    }
    // Gap draws kept (unused) so the seeded sequence matches the reference exactly.
    const gaps: number[] = [];
    const n = 2 + Math.floor(rand() * 3);
    for (let g = 0; g < n; g++) gaps.push(rand());
    rings.push({
      d: softPath(pts, true),
      dash: `${(8 + rand() * 22).toFixed(1)} ${(1.6 + rand() * 2.4).toFixed(1)} ${(14 + rand() * 30).toFixed(1)} ${(1.4 + rand() * 2).toFixed(1)}`,
      off: (rand() * 40).toFixed(1),
    });
  }
  const road: Pt[] = [];
  for (let i = 0; i <= 20; i++) {
    const t = i / 20;
    const y = lerp(104, 52, t);
    const x = 44 + 9 * Math.sin(t * 3.4 + 0.3) - 6 * t;
    const w = lerp(10, 1.2, t);
    road.push([x - w / 2, y]);
  }
  for (let i = 20; i >= 0; i--) {
    const t = i / 20;
    const y = lerp(104, 52, t);
    const x = 44 + 9 * Math.sin(t * 3.4 + 0.3) - 6 * t;
    const w = lerp(10, 1.2, t);
    road.push([x + w / 2, y]);
  }
  return { rings, road: softPath(road, true) };
})();

/** Number of rings in the mark (progress fills them one by one). */
export const WHORL_RINGS = WHORL.rings.length;

/* ── Whorl ──────────────────────────────────────────────────────────── */

export interface WhorlProps {
  size?: number;
  /** 0..1 — rings fill from the centre outwards. */
  progress?: number;
  upper?: string;
  lower?: string;
  title?: string;
  /** When the mark sits beside its own label (e.g. inside <Logo/>), hide it from assistive tech. */
  decorative?: boolean;
  className?: string;
  style?: CSSProperties;
}

const RING_FADE = "opacity 380ms cubic-bezier(.22,1,.36,1)";

export function Whorl({
  size = 56,
  progress = 1,
  upper = "#DAB994",
  lower = "#A67C3D",
  title = "Ládera City",
  decorative = false,
  className,
  style,
}: WhorlProps): JSX.Element {
  // useId() output may contain characters that are awkward inside url(#…); keep it plain.
  const id = `ldw${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const n = WHORL.rings.length;
  const halves: Array<readonly ["u" | "l", string]> = [
    ["u", upper],
    ["l", lower],
  ];
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : title}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      className={className}
      style={{ flex: "none", ...style }}
    >
      <defs>
        <clipPath id={`${id}c`}>
          <circle cx="50" cy="50" r="47" />
        </clipPath>
        <clipPath id={`${id}u`}>
          <path d="M0 0H100V49.2C80 48.6 66 50.2 50 49.4 34 48.6 18 50 0 49.4Z" />
        </clipPath>
        <clipPath id={`${id}l`}>
          <path d="M0 51.6C18 52.2 34 50.8 50 51.6 66 52.4 80 50.8 100 51.4V100H0Z" />
        </clipPath>
        <mask id={`${id}m`}>
          <rect width="100" height="100" fill="#fff" />
          <path d={WHORL.road} fill="#000" />
        </mask>
      </defs>
      <g clipPath={`url(#${id}c)`} fill="none" strokeWidth="2.05" strokeLinecap="round">
        {halves.map(([half, col]) => (
          <g key={half} clipPath={`url(#${id}${half})`} stroke={col} mask={half === "l" ? `url(#${id}m)` : undefined}>
            {WHORL.rings.map((r, i) => {
              const p = clamp(progress * (n + 2) - i);
              return <path key={i} d={r.d} strokeDasharray={r.dash} strokeDashoffset={r.off} opacity={p} style={{ transition: RING_FADE }} />;
            })}
          </g>
        ))}
      </g>
    </svg>
  );
}

/* ── Logo ───────────────────────────────────────────────────────────── */

export interface LogoProps {
  size?: number;
  tone?: "ivory" | "earth";
  compact?: boolean;
  /** Link target. Default "#top". */
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
}

export function Logo({ size = 48, tone = "ivory", compact = false, href = "#top", onClick, className }: LogoProps): JSX.Element {
  return (
    <a
      className={className ? `ld-logo ${className}` : "ld-logo"}
      href={href}
      onClick={onClick}
      aria-label="Ládera City by Indulkar, home"
      style={{ color: tone === "ivory" ? "var(--ivory)" : "var(--earth)" }}
    >
      <Whorl size={size} decorative />
      {!compact && (
        <span className="ld-logo__word" aria-hidden="true">
          <span className="ld-logo__name">Ládera City</span>
          <span className="ld-logo__by">By Indulkar</span>
        </span>
      )}
    </a>
  );
}
